// src/pages/ProductsPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  getProducts,
  deleteProduct,
  setProductVisibility,
} from "../services/productService";
import { getCategories } from "../services/categoryService";

import ProductEditForm from "../components/Product/ProductEditForm";
import ProductTable from "../components/Product/ProductTable";

const PRODUCTS_PER_PAGE = 10;

const ProductsPage = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);

  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  const [creating, setCreating] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  // page з query (за замовчуванням 1)
  const pageFromQuery = Number(searchParams.get("page")) || 1;
  const [page, setPage] = useState(pageFromQuery);

  const loadAll = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        getProducts(), // адмінський список з includeHidden=1
        getCategories(),
      ]);
      setItems(productsRes);
      setCategories(categoriesRes);
    } catch (err) {
      console.error("Помилка завантаження:", err);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  // підтягнути category/page з URL
  useEffect(() => {
    const catFromQuery = searchParams.get("category") || "";
    setFilterCategory(catFromQuery);

    const qPage = Number(searchParams.get("page")) || 1;
    setPage(qPage);
  }, [searchParams]);

  const flatCategories = useMemo(() => {
    const build = (cats, prefix = "") => {
      let result = [];
      for (const c of cats) {
        result.push({ id: c.id, name: prefix + c.name });
        if (c.subcategories?.length) {
          result = result.concat(build(c.subcategories, prefix + "→ "));
        }
      }
      return result;
    };
    return build(categories);
  }, [categories]);

  const getAllChildCategoryIds = (catId, cats) => {
    const ids = [];
    const findCat = (list) => {
      for (const c of list) {
        if (String(c.id) === String(catId)) {
          const collect = (node) => {
            ids.push(node.id);
            if (node.subcategories?.length) node.subcategories.forEach(collect);
          };
          collect(c);
          return;
        }
        if (c.subcategories?.length) findCat(c.subcategories);
      }
    };
    findCat(cats);
    return ids;
  };

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch = (item.name || "")
        .toLowerCase()
        .includes(search.toLowerCase());

      if (!filterCategory) return matchesSearch;

      const validIds = getAllChildCategoryIds(filterCategory, categories).map(String);
      return matchesSearch && validIds.includes(String(item.categoryId));
    });
  }, [items, search, filterCategory, categories]);

  // ---- Pagination (after filtering) ----
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PRODUCTS_PER_PAGE));

  // якщо сторінка стала невалідною (наприклад після фільтра/видалення) — підтиснемо
  useEffect(() => {
    if (page > totalPages) {
      const next = totalPages;
      setPage(next);
      const params = {};
      if (filterCategory) params.category = filterCategory;
      params.page = String(next);
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  const pagedItems = useMemo(() => {
    const start = (page - 1) * PRODUCTS_PER_PAGE;
    return filteredItems.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredItems, page]);

  const setUrlParams = (nextCategory, nextPage) => {
    const params = {};
    if (nextCategory) params.category = nextCategory;
    if (nextPage && nextPage !== 1) params.page = String(nextPage);
    setSearchParams(params);
  };

  const goToPage = (next) => {
    const safe = Math.min(Math.max(1, next), totalPages);
    setPage(safe);
    setUrlParams(filterCategory, safe);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Видалити товар?")) return;
    try {
      await deleteProduct(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      console.error("Помилка видалення товару:", err);
      alert("Не вдалося видалити товар");
    }
  };

  const handleToggleVisibility = async (product) => {
    const nextHidden = !product.isHidden;
    const label = nextHidden ? "приховати" : "показати";
    if (!window.confirm(`Підтвердити: ${label} товар?`)) return;

    // optimistic update
    setItems((prev) =>
      prev.map((p) => (p.id === product.id ? { ...p, isHidden: nextHidden } : p))
    );

    try {
      const updated = await setProductVisibility(product.id, nextHidden);
      if (updated && updated.id) {
        setItems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      }
    } catch (err) {
      console.error("Помилка зміни видимості:", err);
      // rollback
      setItems((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, isHidden: !nextHidden } : p))
      );
      alert("Не вдалося змінити видимість товару");
    }
  };

  const handleSaved = async () => {
    setCreating(false);
    setEditingProduct(null);
    await loadAll();
  };

  // при зміні search — скидаємо сторінку на 1 (але не чіпаємо category)
  useEffect(() => {
    setPage(1);
    setUrlParams(filterCategory, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // ===== Рендер модалки (центроване адаптивне вікно) =====
  const renderModal = (content) => {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6" aria-modal="true" role="dialog">
        <div
          className="absolute inset-0 bg-black/40"
          onClick={() => {
            setCreating(false);
            setEditingProduct(null);
          }}
        />
        <div className="relative w-full max-w-[95vw] sm:max-w-2xl md:max-w-4xl bg-white rounded-2xl shadow-2xl border border-[#D2B48C] max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white/90 backdrop-blur px-5 sm:px-6 py-4 border-b flex items-center justify-between rounded-t-2xl">
            <h3 className="text-lg font-semibold text-[#4B2E2B]">
              {creating ? "Створення товару" : "Редагування товару"}
            </h3>
            <button
              onClick={() => {
                setCreating(false);
                setEditingProduct(null);
              }}
              className="rounded-full w-9 h-9 grid place-items-center border hover:bg-gray-50"
              aria-label="Закрити"
              title="Закрити"
            >
              ×
            </button>
          </div>

          <div className="px-5 sm:px-6 py-5">{content}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 bg-[#F5F0E6] min-h-screen">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-2xl font-bold text-[#4B2E2B]">Products</h2>
        {!creating && !editingProduct && (
          <button
            onClick={() => setCreating(true)}
            className="self-start sm:self-auto bg-[#A47551] text-white px-4 py-2 rounded-2xl hover:bg-[#8B5E3C]"
          >
            Add Product
          </button>
        )}
      </div>

      {!creating && !editingProduct && (
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <input
            className="border rounded p-2 flex-1"
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex gap-2">
            <select
              className="border rounded p-2 min-w-[200px]"
              value={filterCategory}
              onChange={(e) => {
                const value = e.target.value;
                setFilterCategory(value);

                // при зміні категорії — page=1
                setPage(1);
                setUrlParams(value, 1);
              }}
            >
              <option value="">All Categories</option>
              {flatCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>

            <button
              className="px-3 py-2 border rounded hover:bg-gray-100"
              onClick={() => {
                setSearch("");
                setFilterCategory("");
                setPage(1);
                setSearchParams({});
              }}
              title="Скинути фільтри"
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Таблиця */}
      {!creating && !editingProduct && (
        <>
          <ProductTable
            items={pagedItems}
            onEdit={(product) => setEditingProduct(product)}
            onDelete={handleDelete}
            onToggleVisibility={handleToggleVisibility}
          />

          {/* Pagination controls */}
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-sm text-gray-600">
              Показано{" "}
              <span className="font-medium">
                {filteredItems.length === 0
                  ? 0
                  : (page - 1) * PRODUCTS_PER_PAGE + 1}
                –
                {Math.min(page * PRODUCTS_PER_PAGE, filteredItems.length)}
              </span>{" "}
              з <span className="font-medium">{filteredItems.length}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                className={`px-3 py-2 border rounded ${
                  page === 1 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "hover:bg-gray-100"
                }`}
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
                title="Попередня"
              >
                Prev
              </button>

              {/* Невеликий pager з еліпсисом */}
              <Pager page={page} totalPages={totalPages} onChange={goToPage} />

              <button
                className={`px-3 py-2 border rounded ${
                  page === totalPages ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "hover:bg-gray-100"
                }`}
                onClick={() => goToPage(page + 1)}
                disabled={page === totalPages}
                title="Наступна"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Модалка Створення */}
      {creating &&
        renderModal(
          <ProductEditForm
            product={null}
            categories={categories}
            onSaved={handleSaved}
            onCancel={() => setCreating(false)}
          />
        )}

      {/* Модалка Редагування */}
      {editingProduct &&
        renderModal(
          <ProductEditForm
            product={editingProduct}
            categories={categories}
            onSaved={handleSaved}
            onCancel={() => setEditingProduct(null)}
          />
        )}
    </div>
  );
};

function Pager({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  // показуємо: 1 ... (page-1) page (page+1) ... total
  const window = 1;
  const pages = [];

  const push = (p) => pages.push(p);

  push(1);
  if (page - window > 2) push("…");

  for (let p = Math.max(2, page - window); p <= Math.min(totalPages - 1, page + window); p++) {
    push(p);
  }

  if (page + window < totalPages - 1) push("…");
  if (totalPages > 1) push(totalPages);

  const uniq = [];
  for (const p of pages) {
    if (uniq.length === 0 || uniq[uniq.length - 1] !== p) uniq.push(p);
  }

  return (
    <div className="flex items-center gap-1">
      {uniq.map((p, idx) =>
        p === "…" ? (
          <span key={`dots-${idx}`} className="px-2 text-gray-500">
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`px-3 py-2 border rounded ${
              p === page ? "bg-[#4B2E2B] text-white border-[#4B2E2B]" : "hover:bg-gray-100"
            }`}
            title={`Сторінка ${p}`}
          >
            {p}
          </button>
        )
      )}
    </div>
  );
}

export default ProductsPage;


// // src/pages/ProductsPage.js
// import React, { useEffect, useMemo, useState } from "react";
// import { useSearchParams } from "react-router-dom";

// import {
//   getProducts,
//   deleteProduct,
//   setProductVisibility,
// } from "../services/productService";
// import { getCategories } from "../services/categoryService";

// import ProductEditForm from "../components/Product/ProductEditForm";
// import ProductTable from "../components/Product/ProductTable";

// const ProductsPage = () => {
//   const [items, setItems] = useState([]);
//   const [categories, setCategories] = useState([]);

//   const [search, setSearch] = useState("");
//   const [filterCategory, setFilterCategory] = useState("");

//   const [creating, setCreating] = useState(false);
//   const [editingProduct, setEditingProduct] = useState(null);

//   const [searchParams, setSearchParams] = useSearchParams();

//   const loadAll = async () => {
//     try {
//       const [productsRes, categoriesRes] = await Promise.all([
//         getProducts(),     // адмінський список з includeHidden=1
//         getCategories(),
//       ]);
//       setItems(productsRes);
//       setCategories(categoriesRes);
//     } catch (err) {
//       console.error("Помилка завантаження:", err);
//     }
//   };

//   useEffect(() => {
//     loadAll();
//   }, []);

//   useEffect(() => {
//     const catFromQuery = searchParams.get("category");
//     setFilterCategory(catFromQuery || "");
//   }, [searchParams]);

//   const flatCategories = useMemo(() => {
//     const build = (cats, prefix = "") => {
//       let result = [];
//       for (const c of cats) {
//         result.push({ id: c.id, name: prefix + c.name });
//         if (c.subcategories?.length) {
//           result = result.concat(build(c.subcategories, prefix + "→ "));
//         }
//       }
//       return result;
//     };
//     return build(categories);
//   }, [categories]);

//   const getAllChildCategoryIds = (catId, cats) => {
//     const ids = [];
//     const findCat = (list) => {
//       for (const c of list) {
//         if (String(c.id) === String(catId)) {
//           const collect = (node) => {
//             ids.push(node.id);
//             if (node.subcategories?.length) node.subcategories.forEach(collect);
//           };
//           collect(c);
//           return;
//         }
//         if (c.subcategories?.length) findCat(c.subcategories);
//       }
//     };
//     findCat(cats);
//     return ids;
//   };

//   const filteredItems = useMemo(() => {
//     return items.filter((item) => {
//       const matchesSearch = (item.name || "")
//         .toLowerCase()
//         .includes(search.toLowerCase());

//       if (!filterCategory) return matchesSearch;

//       const validIds = getAllChildCategoryIds(filterCategory, categories).map(String);
//       return matchesSearch && validIds.includes(String(item.categoryId));
//     });
//   }, [items, search, filterCategory, categories]);

//   const handleDelete = async (id) => {
//     if (!window.confirm("Видалити товар?")) return;
//     try {
//       await deleteProduct(id);
//       setItems((prev) => prev.filter((i) => i.id !== id));
//     } catch (err) {
//       console.error("Помилка видалення товару:", err);
//       alert("Не вдалося видалити товар");
//     }
//   };

//   const handleToggleVisibility = async (product) => {
//     const nextHidden = !product.isHidden;
//     const label = nextHidden ? "приховати" : "показати";
//     if (!window.confirm(`Підтвердити: ${label} товар?`)) return;

//     // optimistic update
//     setItems((prev) =>
//       prev.map((p) => (p.id === product.id ? { ...p, isHidden: nextHidden } : p))
//     );

//     try {
//       const updated = await setProductVisibility(product.id, nextHidden);

//       // якщо API повертає актуальний продукт — підставимо його (надійніше)
//       if (updated && updated.id) {
//         setItems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
//       }
//     } catch (err) {
//       console.error("Помилка зміни видимості:", err);

//       // rollback
//       setItems((prev) =>
//         prev.map((p) => (p.id === product.id ? { ...p, isHidden: !nextHidden } : p))
//       );

//       alert("Не вдалося змінити видимість товару");
//     }
//   };

//   const handleSaved = async () => {
//     setCreating(false);
//     setEditingProduct(null);
//     await loadAll();
//   };

//   // ===== Рендер модалки (центроване адаптивне вікно) =====
//   const renderModal = (content) => {
//     return (
//       <div
//         className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
//         aria-modal="true"
//         role="dialog"
//       >
//         {/* Backdrop */}
//         <div
//           className="absolute inset-0 bg-black/40"
//           onClick={() => {
//             setCreating(false);
//             setEditingProduct(null);
//           }}
//         />
//         {/* Вікно */}
//         <div className="relative w-full max-w-[95vw] sm:max-w-2xl md:max-w-4xl bg-white rounded-2xl shadow-2xl border border-[#D2B48C] max-h-[90vh] overflow-y-auto">
//           {/* Заголовок + кнопка закриття */}
//           <div className="sticky top-0 bg-white/90 backdrop-blur px-5 sm:px-6 py-4 border-b flex items-center justify-between rounded-t-2xl">
//             <h3 className="text-lg font-semibold text-[#4B2E2B]">
//               {creating ? "Створення товару" : "Редагування товару"}
//             </h3>
//             <button
//               onClick={() => {
//                 setCreating(false);
//                 setEditingProduct(null);
//               }}
//               className="rounded-full w-9 h-9 grid place-items-center border hover:bg-gray-50"
//               aria-label="Закрити"
//               title="Закрити"
//             >
//               ×
//             </button>
//           </div>

//           <div className="px-5 sm:px-6 py-5">{content}</div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <div className="p-4 sm:p-6 bg-[#F5F0E6] min-h-screen">
//       <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
//         <h2 className="text-2xl font-bold text-[#4B2E2B]">Products</h2>
//         {!creating && !editingProduct && (
//           <button
//             onClick={() => setCreating(true)}
//             className="self-start sm:self-auto bg-[#A47551] text-white px-4 py-2 rounded-2xl hover:bg-[#8B5E3C]"
//           >
//             Add Product
//           </button>
//         )}
//       </div>

//       {!creating && !editingProduct && (
//         <div className="flex flex-col sm:flex-row gap-2 mb-4">
//           <input
//             className="border rounded p-2 flex-1"
//             placeholder="Search by name"
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//           />
//           <div className="flex gap-2">
//             <select
//               className="border rounded p-2 min-w-[200px]"
//               value={filterCategory}
//               onChange={(e) => {
//                 const value = e.target.value;
//                 setFilterCategory(value);
//                 if (value) setSearchParams({ category: value });
//                 else setSearchParams({});
//               }}
//             >
//               <option value="">All Categories</option>
//               {flatCategories.map((c) => (
//                 <option key={c.id} value={c.id}>
//                   {c.name}
//                 </option>
//               ))}
//             </select>
//             <button
//               className="px-3 py-2 border rounded hover:bg-gray-100"
//               onClick={() => {
//                 setSearch("");
//                 setFilterCategory("");
//                 setSearchParams({});
//               }}
//               title="Скинути фільтри"
//             >
//               Reset
//             </button>
//           </div>
//         </div>
//       )}

//       {/* Таблиця */}
//       {!creating && !editingProduct && (
//         <ProductTable
//           items={filteredItems}
//           onEdit={(product) => setEditingProduct(product)}
//           onDelete={handleDelete}
//           onToggleVisibility={handleToggleVisibility}
//         />
//       )}

//       {/* Модалка Створення */}
//       {creating &&
//         renderModal(
//           <ProductEditForm
//             product={null}
//             categories={categories}
//             onSaved={handleSaved}
//             onCancel={() => setCreating(false)}
//           />
//         )}

//       {/* Модалка Редагування */}
//       {editingProduct &&
//         renderModal(
//           <ProductEditForm
//             product={editingProduct}
//             categories={categories}
//             onSaved={handleSaved}
//             onCancel={() => setEditingProduct(null)}
//           />
//         )}
//     </div>
//   );
// };

// export default ProductsPage;



// // src/pages/ProductsPage.js
// import React, { useEffect, useMemo, useState } from "react";
// import { useSearchParams } from "react-router-dom";

// import { getProducts, deleteProduct } from "../services/productService";
// import { getCategories } from "../services/categoryService";

// import ProductEditForm from "../components/Product/ProductEditForm";
// import ProductTable from "../components/Product/ProductTable";

// const ProductsPage = () => {
//   const [items, setItems] = useState([]);
//   const [categories, setCategories] = useState([]);

//   const [search, setSearch] = useState("");
//   const [filterCategory, setFilterCategory] = useState("");

//   const [creating, setCreating] = useState(false);
//   const [editingProduct, setEditingProduct] = useState(null);

//   const [searchParams, setSearchParams] = useSearchParams();

//   const loadAll = async () => {
//     try {
//       const [productsRes, categoriesRes] = await Promise.all([
//         getProducts(),
//         getCategories(),
//       ]);
//       setItems(productsRes);
//       setCategories(categoriesRes);
//     } catch (err) {
//       console.error("Помилка завантаження:", err);
//     }
//   };

//   useEffect(() => {
//     loadAll();
//   }, []);

//   useEffect(() => {
//     const catFromQuery = searchParams.get("category");
//     setFilterCategory(catFromQuery || "");
//   }, [searchParams]);

//   const flatCategories = useMemo(() => {
//     const build = (cats, prefix = "") => {
//       let result = [];
//       for (const c of cats) {
//         result.push({ id: c.id, name: prefix + c.name });
//         if (c.subcategories?.length) {
//           result = result.concat(build(c.subcategories, prefix + "→ "));
//         }
//       }
//       return result;
//     };
//     return build(categories);
//   }, [categories]);

//   const getAllChildCategoryIds = (catId, cats) => {
//     const ids = [];
//     const findCat = (list) => {
//       for (const c of list) {
//         if (String(c.id) === String(catId)) {
//           const collect = (node) => {
//             ids.push(node.id);
//             if (node.subcategories?.length) node.subcategories.forEach(collect);
//           };
//           collect(c);
//           return;
//         }
//         if (c.subcategories?.length) findCat(c.subcategories);
//       }
//     };
//     findCat(cats);
//     return ids;
//   };

//   const filteredItems = useMemo(() => {
//     return items.filter((item) => {
//       const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
//       if (!filterCategory) return matchesSearch;
//       const validIds = getAllChildCategoryIds(filterCategory, categories).map(String);
//       return matchesSearch && validIds.includes(String(item.categoryId));
//     });
//   }, [items, search, filterCategory, categories]);

//   const handleDelete = async (id) => {
//     if (!window.confirm("Видалити товар?")) return;
//     try {
//       await deleteProduct(id);
//       setItems((prev) => prev.filter((i) => i.id !== id));
//     } catch (err) {
//       console.error("Помилка видалення товару:", err);
//       alert("Не вдалося видалити товар");
//     }
//   };

//   const handleSaved = async () => {
//     setCreating(false);
//     setEditingProduct(null);
//     await loadAll();
//   };

//   // ===== Рендер модалки (центроване адаптивне вікно) =====
//   const renderModal = (content) => {
//     return (
//       <div
//         className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
//         aria-modal="true"
//         role="dialog"
//       >
//         {/* Backdrop */}
//         <div
//           className="absolute inset-0 bg-black/40"
//           onClick={() => {
//             setCreating(false);
//             setEditingProduct(null);
//           }}
//         />
//         {/* Вікно */}
//         <div
//           className="relative w-full max-w-[95vw] sm:max-w-2xl md:max-w-4xl bg-white rounded-2xl shadow-2xl border border-[#D2B48C] max-h-[90vh] overflow-y-auto"
//         >
//           {/* Заголовок + кнопка закриття */}
//           <div className="sticky top-0 bg-white/90 backdrop-blur px-5 sm:px-6 py-4 border-b flex items-center justify-between rounded-t-2xl">
//             <h3 className="text-lg font-semibold text-[#4B2E2B]">
//               {creating ? "Створення товару" : "Редагування товару"}
//             </h3>
//             <button
//               onClick={() => {
//                 setCreating(false);
//                 setEditingProduct(null);
//               }}
//               className="rounded-full w-9 h-9 grid place-items-center border hover:bg-gray-50"
//               aria-label="Закрити"
//               title="Закрити"
//             >
//               ×
//             </button>
//           </div>

//           <div className="px-5 sm:px-6 py-5">{content}</div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <div className="p-4 sm:p-6 bg-[#F5F0E6] min-h-screen">
//       <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
//         <h2 className="text-2xl font-bold text-[#4B2E2B]">Products</h2>
//         {!creating && !editingProduct && (
//           <button
//             onClick={() => setCreating(true)}
//             className="self-start sm:self-auto bg-[#A47551] text-white px-4 py-2 rounded-2xl hover:bg-[#8B5E3C]"
//           >
//             Add Product
//           </button>
//         )}
//       </div>

//       {!creating && !editingProduct && (
//         <div className="flex flex-col sm:flex-row gap-2 mb-4">
//           <input
//             className="border rounded p-2 flex-1"
//             placeholder="Search by name"
//             value={search}
//             onChange={(e) => setSearch(e.target.value)}
//           />
//           <div className="flex gap-2">
//             <select
//               className="border rounded p-2 min-w-[200px]"
//               value={filterCategory}
//               onChange={(e) => {
//                 const value = e.target.value;
//                 setFilterCategory(value);
//                 if (value) setSearchParams({ category: value });
//                 else setSearchParams({});
//               }}
//             >
//               <option value="">All Categories</option>
//               {flatCategories.map((c) => (
//                 <option key={c.id} value={c.id}>
//                   {c.name}
//                 </option>
//               ))}
//             </select>
//             <button
//               className="px-3 py-2 border rounded hover:bg-gray-100"
//               onClick={() => {
//                 setSearch("");
//                 setFilterCategory("");
//                 setSearchParams({});
//               }}
//               title="Скинути фільтри"
//             >
//               Reset
//             </button>
//           </div>
//         </div>
//       )}

//       {/* Таблиця */}
//       {!creating && !editingProduct && (
//         <ProductTable
//           items={filteredItems}
//           onEdit={(product) => setEditingProduct(product)}
//           onDelete={handleDelete}
//         />
//       )}

//       {/* Модалка Створення */}
//       {creating &&
//         renderModal(
//           <ProductEditForm
//             product={null}
//             categories={categories}
//             onSaved={handleSaved}
//             onCancel={() => setCreating(false)}
//           />
//         )}

//       {/* Модалка Редагування */}
//       {editingProduct &&
//         renderModal(
//           <ProductEditForm
//             product={editingProduct}
//             categories={categories}
//             onSaved={handleSaved}
//             onCancel={() => setEditingProduct(null)}
//           />
//         )}
//     </div>
//   );
// };

// export default ProductsPage;


